import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  FunnelStageTone, FunnelStageOutcome,
  OpportunityStatus, OpportunitySource, OpportunityEventType, OpportunityEventActor,
} from '@evo-hub/shared';
import { api } from '../lib/api.js';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface FunnelStage {
  id: number;
  funnelId: number;
  name: string;
  position: number;
  tone: FunnelStageTone;
  outcome: FunnelStageOutcome | null;
}

/** La etapa con sus números — solo la devuelve el endpoint del tablero. */
export interface FunnelStageWithTotals extends FunnelStage {
  count: number;
  totalCents: number;
}

export interface Funnel {
  id: number;
  name: string;
  description: string | null;
  position: number;
  archived: number;
  createdAt: string;
  updatedAt: string;
  stages: FunnelStage[];
}

/** Lo que agrega la lista del módulo: los números y de qué formulario come. */
export interface FunnelListItem extends Funnel {
  openCount: number;
  openTotalCents: number;
  /** Todas las oportunidades, también ganadas y perdidas: lo que se lleva un borrado. */
  opportunityCount: number;
  forms: Array<{ id: string; name: string }>;
}

export interface Opportunity {
  id: number;
  funnelId: number;
  stageId: number;
  position: number;
  title: string;
  valueCents: number;
  currency: string;
  status: OpportunityStatus;
  contactId: number | null;
  leadId: string | null;
  submissionId: number | null;
  estimateId: number | null;
  projectId: number | null;
  source: OpportunitySource;
  notes: string | null;
  stageEnteredAt: string;
  createdAt: string;
  updatedAt: string;
  // Nombres resueltos por el servidor: la carta los muestra sin pedir cuatro
  // entidades más por su cuenta.
  contactName: string | null;
  formName: string | null;
  estimateNumber: string | null;
  projectName: string | null;
}

export interface OpportunityEvent {
  id: number;
  opportunityId: number;
  type: OpportunityEventType;
  fromStageId: number | null;
  toStageId: number | null;
  actor: OpportunityEventActor;
  metadataJson: string | null;
  createdAt: string;
}

export interface BoardData {
  funnel: Funnel;
  stages: FunnelStageWithTotals[];
  opportunities: Opportunity[];
}

export interface StageInput {
  id?: number;
  name: string;
  tone: FunnelStageTone;
  outcome: FunnelStageOutcome | null;
}

// ── Embudos ───────────────────────────────────────────────────────────────────

export function useFunnels() {
  return useQuery<{ funnels: FunnelListItem[] }>({
    queryKey: ['funnels'],
    queryFn: () => api.get<{ funnels: FunnelListItem[] }>('/api/funnels'),
  });
}

export function useFunnel(id: number | null) {
  return useQuery<Funnel>({
    queryKey: ['funnel', id],
    queryFn: () => api.get<Funnel>(`/api/funnels/${id}`),
    enabled: id != null,
  });
}

export function useCreateFunnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string | null; stages?: StageInput[] }) =>
      api.post<Funnel>('/api/funnels', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['funnels'] }),
  });
}

export function useUpdateFunnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name?: string; description?: string | null; archived?: boolean }) =>
      api.put<Funnel>(`/api/funnels/${id}`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['funnels'] });
      void qc.invalidateQueries({ queryKey: ['funnel', vars.id] });
    },
  });
}

export function useDeleteFunnel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/funnels/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['funnels'] }),
  });
}

export function useReplaceStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ funnelId, stages }: { funnelId: number; stages: StageInput[] }) =>
      api.put<{ stages: FunnelStage[] }>(`/api/funnels/${funnelId}/stages`, { stages }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['funnels'] });
      void qc.invalidateQueries({ queryKey: ['funnel', vars.funnelId] });
      void qc.invalidateQueries({ queryKey: ['board', vars.funnelId] });
    },
  });
}

// ── Tablero ───────────────────────────────────────────────────────────────────

export function useBoard(funnelId: number | null, status?: OpportunityStatus) {
  return useQuery<BoardData>({
    queryKey: ['board', funnelId, status ?? 'all'],
    queryFn: () => api.get<BoardData>(
      `/api/funnels/${funnelId}/opportunities${status ? `?status=${status}` : ''}`,
    ),
    enabled: funnelId != null,
  });
}

// ── Oportunidades ─────────────────────────────────────────────────────────────

export function useOpportunity(id: number | null) {
  return useQuery<{ opportunity: Opportunity; events: OpportunityEvent[]; stages: FunnelStage[] }>({
    queryKey: ['opportunity', id],
    queryFn: () => api.get<{ opportunity: Opportunity; events: OpportunityEvent[]; stages: FunnelStage[] }>(
      `/api/opportunities/${id}`,
    ),
    enabled: id != null,
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      funnelId: number; stageId?: number; title: string; valueCents?: number;
      contactId?: number | null; notes?: string | null;
    }) => api.post<Opportunity>('/api/opportunities', body),
    onSuccess: (_data, vars) => void qc.invalidateQueries({ queryKey: ['board', vars.funnelId] }),
  });
}

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: number; title?: string; valueCents?: number; notes?: string | null;
      contactId?: number | null; estimateId?: number | null; projectId?: number | null;
    }) => api.patch<Opportunity>(`/api/opportunities/${id}`, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['opportunity', data.id] });
      void qc.invalidateQueries({ queryKey: ['board', data.funnelId] });
    },
  });
}

/**
 * Mueve la carta. El caller pinta el movimiento antes de llamar (optimista) y
 * revierte si esto falla — por eso acá no se invalida el tablero en `onSuccess`
 * con la carta ya en su lugar, sino que se refresca al final del intento.
 */
export function useMoveOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId, index }: { id: number; stageId: number; index: number }) =>
      api.patch<Opportunity>(`/api/opportunities/${id}/move`, { stageId, index }),
    onSettled: (data) => {
      if (data) {
        void qc.invalidateQueries({ queryKey: ['opportunity', data.id] });
        void qc.invalidateQueries({ queryKey: ['board', data.funnelId] });
      } else {
        void qc.invalidateQueries({ queryKey: ['board'] });
      }
    },
  });
}

export function useDeleteOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/opportunities/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['board'] }),
  });
}

/** Enlace de vuelta desde una cotización o un proyecto a su oportunidad. */
export function useOpportunityByEstimate(estimateId: number | null) {
  return useQuery<{ opportunity: Opportunity | null }>({
    queryKey: ['opportunity-by-estimate', estimateId],
    queryFn: () => api.get<{ opportunity: Opportunity | null }>(`/api/opportunities/by-estimate/${estimateId}`),
    enabled: estimateId != null,
  });
}

export function useOpportunityByProject(projectId: number | null) {
  return useQuery<{ opportunity: Opportunity | null }>({
    queryKey: ['opportunity-by-project', projectId],
    queryFn: () => api.get<{ opportunity: Opportunity | null }>(`/api/opportunities/by-project/${projectId}`),
    enabled: projectId != null,
  });
}

// ── Acciones de la carta ──────────────────────────────────────────────────────
// Las dos las resuelve el servidor en una sola llamada: crear el contacto por
// un lado y la cotización por otro dejaría un contacto suelto si la segunda
// mitad falla.

export function useOpportunityToContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ contactId: number; created: boolean }>(`/api/opportunities/${id}/contact`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['board'] });
      void qc.invalidateQueries({ queryKey: ['opportunity', id] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useOpportunityToEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ estimateId: number; contactId: number }>(`/api/opportunities/${id}/estimate`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['board'] });
      void qc.invalidateQueries({ queryKey: ['opportunity', id] });
      void qc.invalidateQueries({ queryKey: ['estimates'] });
    },
  });
}

// ── Acciones en lote (vista de lista) ─────────────────────────────────────────

export interface BulkConvertResult {
  created: number;
  linked: number;
  skipped: number;
  reasons: string[];
}

export function useBulkMoveOpportunities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, stageId }: { ids: number[]; stageId: number }) =>
      api.patch<{ moved: number }>('/api/opportunities/bulk/move', { ids, stageId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['board'] }),
  });
}

export function useBulkOpportunityToContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.post<BulkConvertResult>('/api/opportunities/bulk/contact', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['board'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}
